import { TFunction } from "i18next";

import { IconEdit01, IconStar01, IconTrash01 } from "@pentabd/icons";
import { Badge, Text } from "@pentabd/ui";

import { ProfileInfoCard } from "@components";

import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import { getPositionColor } from "@helpers/getPositionColor";
import { capitalizeFirstLetter } from "@utils/capitalize";

import { MASTER_DATA } from "@constants/master-data";

import { DesignationType } from "@type/HRM/designation";
import { GroupType } from "@type/HRM/group";
import { PositionType } from "@type/HRM/position";
import { SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";

const { POST_TYPE } = MASTER_DATA;

export const TableColumn = (
  t: TFunction<"translation", undefined>,
  setIsConfirmationModalOpen: (value: SetStateAction<boolean>) => void,
  setItemId: (value: SetStateAction<number | undefined>) => void,
  roles: string[],
  getAccess: (roles: string[], roleType: string) => boolean,
  navigate: NavigateFunction,
) => {
  const { data: postType } = useMasterData(POST_TYPE);
  const { language } = useLanguage();

  return [
    {
      id: 1,
      key: "isHead",
      name: "",
      render: (isHead: boolean) => (
        <div className="d-flex flex-column">
          {isHead ? <IconStar01 size={24} fill="warning" /> : <></>}
        </div>
      ),
    },
    {
      id: 2,
      key: "position",
      name: t("POSITION_LIST.TABLE.COLUMN.NAME"),
      render: (position: DesignationType, row: PositionType) => (
        <div className="d-flex flex-column">
          <Text color={`${getPositionColor(postType, row)}`}>
            {language === "en" ? position.name : position.nameBn}
          </Text>
          <Text weight="bold" color="info">
            {language === "en" ? row.unit.name : row.unit.bnName}
          </Text>
        </div>
      ),
    },
    {
      id: 3,
      key: "gender",
      name: t("POSITION_LIST.TABLE.COLUMN.ASSIGNED_EMPLOYEE"),
      render: (gender: string, row: PositionType) => {
        const { id } = row.employee || {};
        return !!id ? (
          <ProfileInfoCard employee={row.employee} viewProfile />
        ) : (
          <Badge label={`Empty`} type="danger" size="sm" className="w-max" />
        );
      },
    },
    {
      id: 4,
      key: "groupIds",
      name: t("POSITION_LIST.TABLE.COLUMN.GROUPS"),
      render: (groupIds: GroupType) => (
        <div className="d-flex gap-2">
          {groupIds?.map((item: any, i: number) => (
            <Badge
              className="text-nowrap"
              key={i}
              size="sm"
              type="warning"
              label={capitalizeFirstLetter(item?.name).replaceAll("_", " ")}
            />
          ))}
        </div>
      ),
    },
    {
      id: 5,
      key: "id",
      name: "",
      render: (id: number | string) => {
        return (
          <div
            className="d-flex gap-3 
align-items-center"
          >
            <div
              onClick={() => {
                navigate(`${id}`);
              }}
              className="cursor-pointer"
            >
              <IconEdit01 size={24} fill="subtitle1" />
            </div>
            {getAccess(roles, "POSITION_MODIFY_PAGE") ? (
              <div
                onClick={() => {
                  setIsConfirmationModalOpen(true);
                  setItemId(id as number);
                }}
                className="cursor-pointer"
              >
                <IconTrash01 size={24} fill="danger" />
              </div>
            ) : null}
          </div>
        );
      },
    },
  ];
};
